import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import Paginations from "../../components/Common/Pagination";
import Spinners from "../../components/Common/Spinner";
import { useDebounce } from "../../hooks/useDebounce";
import {
  useCreateSupervisor,
  useSetSupervisorActive,
  useSupervisorsList,
  useUpdateSupervisor,
} from "../../api/supervisors";

const ACTIVE_FILTER = {
  ALL: "all",
  ACTIVE: "active",
  INACTIVE: "inactive",
};

function parseActiveFilter(value) {
  if (value === ACTIVE_FILTER.ACTIVE) return true;
  if (value === ACTIVE_FILTER.INACTIVE) return false;
  return undefined;
}

const ClientSupervisors = () => {
  document.title = "Supervisor Management | Client";

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [activeFilter, setActiveFilter] = useState(ACTIVE_FILTER.ALL);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 400);

  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const [formFullName, setFormFullName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");

  const isActiveParam = parseActiveFilter(activeFilter);

  const { data, isLoading, isFetching, error, refetch } = useSupervisorsList({
    page,
    limit,
    isActive: isActiveParam,
    search: debouncedSearch.trim() || undefined,
  });

  const createMutation = useCreateSupervisor();
  const updateMutation = useUpdateSupervisor();
  const setActiveMutation = useSetSupervisorActive();

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeFilter]);

  const nested = data?.data ?? data;
  const rows = Array.isArray(nested?.data) ? nested.data : [];
  const total = nested?.total ?? 0;

  const loading = isLoading || isFetching;

  const resetForm = () => {
    setFormFullName("");
    setFormEmail("");
    setFormPhone("");
  };

  const openCreate = () => {
    resetForm();
    setCreateOpen(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setFormFullName(row.fullName || "");
    setFormPhone(row.phoneNumber || "");
  };

  const closeCreate = () => {
    setCreateOpen(false);
    resetForm();
  };

  const closeEdit = () => {
    setEditRow(null);
    resetForm();
  };

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(
      {
        fullName: formFullName.trim(),
        email: formEmail.trim(),
        phoneNumber: formPhone.trim(),
      },
      {
        onSuccess: () => {
          closeCreate();
        },
      }
    );
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!editRow?.slug) return;
    updateMutation.mutate(
      {
        slug: editRow.slug,
        payload: {
          fullName: formFullName.trim(),
          phoneNumber: formPhone.trim(),
        },
      },
      {
        onSuccess: () => {
          closeEdit();
        },
      }
    );
  };

  const toggleActive = (row) => {
    const next = !row.supervisorIsActive;
    setActiveMutation.mutate({ slug: row.slug, isActive: next });
  };

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "fullName",
        enableColumnFilter: false,
        cell: (info) => info.getValue() || "-",
      },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
      },
      {
        header: "Phone",
        accessorKey: "phoneNumber",
        enableColumnFilter: false,
        cell: (info) => info.getValue() || "-",
      },
      {
        header: "Status",
        accessorKey: "supervisorIsActive",
        enableColumnFilter: false,
        cell: (info) => {
          const active = info.getValue();
          return (
            <Badge color={active ? "success" : "secondary"}>
              {active ? "Active" : "Inactive"}
            </Badge>
          );
        },
      },
      {
        header: "Created",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue()
            ? dayjs(info.getValue()).format("DD MMM YYYY HH:mm")
            : "-",
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="d-flex flex-wrap gap-1">
              <Button
                size="sm"
                color="primary"
                outline
                onClick={() => openEdit(row)}
                disabled={setActiveMutation.isPending}
              >
                Edit
              </Button>
              <Button
                size="sm"
                color={row.supervisorIsActive ? "warning" : "success"}
                outline
                onClick={() => toggleActive(row)}
                disabled={setActiveMutation.isPending}
              >
                {row.supervisorIsActive ? "Deactivate" : "Activate"}
              </Button>
            </div>
          );
        },
      },
    ],
    [setActiveMutation.isPending]
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          title="Supervisors"
          breadcrumbItem="Supervisor management"
        />

        <Row className="mb-3 g-2 align-items-center">
          <Col md={4}>
            <Input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Input
              type="select"
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value={ACTIVE_FILTER.ALL}>All</option>
              <option value={ACTIVE_FILTER.ACTIVE}>Active only</option>
              <option value={ACTIVE_FILTER.INACTIVE}>Inactive only</option>
            </Input>
          </Col>
          <Col md={2}>
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Input>
          </Col>
          <Col md="auto" className="ms-auto d-flex gap-2 flex-wrap">
            <Button color="primary" onClick={openCreate}>
              <i className="bx bx-plus me-1" />
              Add supervisor
            </Button>
            <Button
              color="light"
              onClick={() => refetch()}
              disabled={loading}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading && rows.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : error ? (
              <div className="text-danger">Failed to load supervisors.</div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={rows}
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />

                {total > 0 && (
                  <Paginations
                    total={total}
                    perPageData={limit}
                    currentPage={page}
                    setCurrentPage={setPage}
                    isShowingPageLength
                    paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                    paginationClass="pagination"
                  />
                )}
              </>
            )}
          </CardBody>
        </Card>
      </div>

      <Modal isOpen={createOpen} toggle={closeCreate} centered>
        <ModalHeader toggle={closeCreate}>Add supervisor</ModalHeader>
        <Form onSubmit={handleCreateSubmit}>
          <ModalBody>
            <FormGroup>
              <Label for="create-fullName">Full name</Label>
              <Input
                id="create-fullName"
                required
                value={formFullName}
                onChange={(e) => setFormFullName(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label for="create-email">Email</Label>
              <Input
                id="create-email"
                type="email"
                required
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label for="create-phone">Phone number</Label>
              <Input
                id="create-phone"
                type="tel"
                required
                value={formPhone}
                onChange={(e) => setFormPhone(e.target.value)}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={closeCreate}>
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Saving…" : "Create"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={!!editRow} toggle={closeEdit} centered>
        <ModalHeader toggle={closeEdit}>Edit supervisor</ModalHeader>
        <Form onSubmit={handleEditSubmit}>
          <ModalBody>
            <p className="text-muted small mb-3">
              Email cannot be changed. Update the name and phone number below.
            </p>
            <FormGroup>
              <Label for="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                disabled
                value={editRow?.email || ""}
              />
            </FormGroup>
            <FormGroup>
              <Label for="edit-fullName">Full name</Label>
              <Input
                id="edit-fullName"
                required
                value={formFullName}
                onChange={(e) => setFormFullName(e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label for="edit-phone">Phone number</Label>
              <Input
                id="edit-phone"
                type="tel"
                required
                value={formPhone}
                onChange={(e) => setFormPhone(e.target.value)}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={closeEdit}>
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default ClientSupervisors;
